/**
 * Unsaved changes — one guard for every form in the agent app (phone and
 * desktop shells alike). Mirrors cid_admin_portal's context of the same name.
 *
 * Owner, 2026-10-02: "we should have some mechanism to avoid user leave the page
 * with unsaved data. Apply to all forms." Each form only REPORTS that it has
 * unsaved changes (`useUnsavedChanges(dirty, …)`); this provider is the one place
 * that stops the user leaving:
 *
 *  - Inside the app (sidebar, back arrow, browser Back, a link): React Router's
 *    `useBlocker` holds the navigation and the provider asks — keep editing,
 *    discard, or (when the form can) save and go. A router supports ONE blocker
 *    at a time, which is why there is a single provider rather than a blocker per
 *    form: a dirty modal sitting on a dirty page would otherwise fight it.
 *    `useBlocker` needs a data router — main.jsx builds one.
 *  - Learn Mode is exempt: its lessons run on the real pages against sandbox data
 *    and navigate on their own, so nothing it types is worth stopping for.
 *  - Out of the app (reload, closing the tab, typing a URL): only the browser's
 *    own "Leave site?" dialog can interrupt that, via `beforeunload`. **iPad and
 *    iPhone Safari never show it**; the long forms therefore also keep a device
 *    copy (hooks/useFormAutosave) that the reloaded page restores.
 *  - Closing a modal or drawer that holds typing: `confirmClose(close)` asks
 *    before throwing it away.
 *
 * Registrations are read through refs at the moment of navigating, so a form that
 * has just saved can `release()` and navigate in the same tick.
 */
import { createContext, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useBlocker, useLocation } from 'react-router-dom';
import { App, Button, Modal, Space, Typography } from 'antd';

const { Text } = Typography;
const UnsavedChangesContext = createContext(null);

// Learn Mode (learn/TourContext) marks the body while a lesson runs.
const inLesson = () => document.body.classList.contains('learn-active');

export function UnsavedChangesProvider({ children }) {
  // id → { dirty, released, opts } — mutated in place by useUnsavedChanges.
  const regs = useRef(new Map());
  const passOnce = useRef(false);
  const [saving, setSaving] = useState(false);

  const pendingNow = () => (inLesson() ? [] : [...regs.current.values()].filter((r) => r.dirty && !r.released));

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!pendingNow().length) return;
      e.preventDefault();
      // Older Chromium still needs returnValue set to show the dialog.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // A form that outlives a change of ?tab= (a non-modal drawer, say) registers
  // `pathOnly` and is only at risk when the page itself changes.
  const pendingFor = (from, to) => {
    if (from.pathname === to.pathname && from.search === to.search) return [];
    const samePage = from.pathname === to.pathname;
    return pendingNow().filter((r) => !(samePage && r.opts.pathOnly));
  };

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (passOnce.current) {
      passOnce.current = false;
      return false;
    }
    return pendingFor(currentLocation, nextLocation).length > 0;
  });
  const location = useLocation();

  const api = useMemo(() => ({
    set: (id, entry) => { regs.current.set(id, entry); },
    remove: (id) => { regs.current.delete(id); },
    allowOnce: () => { passOnce.current = true; },
  }), []);

  const pending = blocker.state === 'blocked' ? pendingFor(location, blocker.location) : [];
  const first = pending[0];
  const canSave = pending.length === 1 && !!first?.opts.onSave;

  const stay = () => { if (blocker.state === 'blocked') blocker.reset(); };
  const discard = () => {
    pending.forEach((r) => { r.released = true; r.opts.onDiscard?.(); });
    if (blocker.state === 'blocked') blocker.proceed();
  };
  const saveAndLeave = async () => {
    setSaving(true);
    try {
      const ok = await first.opts.onSave();
      if (ok === false) return; // the form said why; stay on it
      first.released = true;
      if (blocker.state === 'blocked') blocker.proceed();
    } finally {
      setSaving(false);
    }
  };

  const what = first?.opts.what || 'this form';
  return (
    <UnsavedChangesContext.Provider value={api}>
      {children}
      <Modal
        open={blocker.state === 'blocked' && pending.length > 0}
        title="Leave without saving?"
        centered
        onCancel={stay}
        maskClosable={false}
        footer={(
          <Space wrap>
            <Button danger onClick={discard} disabled={saving}>Discard changes</Button>
            <Button onClick={stay} disabled={saving}>Keep editing</Button>
            {canSave && (
              <Button type="primary" loading={saving} onClick={saveAndLeave}>{first.opts.saveLabel || 'Save & leave'}</Button>
            )}
          </Space>
        )}
      >
        <Text>
          You have changes on {what} that are not saved yet.
          {first?.opts.keptOnDevice ? ' They are kept on this device, so they will come back if you open it here again — but nobody else can see them.' : ''}
        </Text>
      </Modal>
    </UnsavedChangesContext.Provider>
  );
}

/**
 * Report a form's unsaved changes to the guard.
 *
 *   const guard = useUnsavedChanges(dirty, {
 *     what: 'this OTP',              // "You have changes on this OTP…"
 *     onSave: async () => boolean,  // optional — offers "Save & leave"
 *     saveLabel: 'Save draft & leave',
 *     onDiscard: () => {},          // optional — e.g. drop the device copy
 *     keptOnDevice: true,           // optional — says the typing survives on this device
 *     pathOnly: true,               // optional — survives ?query changes (e.g. a tab switch)
 *   });
 *   guard.release();               // saved: let the next navigation through
 *   guard.allowOnce();             // the page moving its own URL, not leaving
 *   guard.confirmClose(close);     // modal/drawer Cancel, ×, mask click
 *
 * Pass `dirty && open` for a modal, so a closed one never blocks anything.
 */
export function useUnsavedChanges(dirty, opts = {}) {
  const ctx = useContext(UnsavedChangesContext);
  const id = useId();
  const { modal } = App.useApp();
  const entry = useRef({ dirty: false, released: false, opts });

  // Kept current after every commit — before any click can be handled — rather
  // than written during render.
  useLayoutEffect(() => {
    const e = entry.current;
    // A fresh change after a release (or after a save) re-arms the guard.
    if (dirty && !e.dirty) e.released = false;
    e.dirty = !!dirty;
    e.opts = opts;
  });

  useEffect(() => {
    if (!ctx) return undefined;
    ctx.set(id, entry.current);
    return () => ctx.remove(id);
  }, [ctx, id]);

  return useMemo(() => ({
    release: () => { entry.current.released = true; },
    allowOnce: () => { ctx?.allowOnce(); },
    confirmClose: (close) => {
      const e = entry.current;
      if (!e.dirty || e.released || inLesson()) { close(); return; }
      modal.confirm({
        title: 'Discard your changes?',
        content: `What you typed in ${e.opts.what || 'this form'} has not been saved and will be lost.`,
        okText: 'Discard',
        okButtonProps: { danger: true },
        cancelText: 'Keep editing',
        centered: true,
        onOk: () => {
          e.released = true;
          e.opts.onDiscard?.();
          close();
        },
      });
    },
  }), [ctx, modal]);
}
